import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription, distinctUntilChanged } from 'rxjs';
import { authFeature } from '../../features/auth/state';
import { readJwtExpiry } from '../utils/jwt';
import { TokenRefreshService } from './token-refresh.service';

// How long before the JWT expires the renewal is attempted.
export const RENEW_BEFORE_EXPIRY_MS = 60_000;
// setTimeout stores its delay in a signed 32-bit integer; anything longer fires immediately.
export const MAX_TIMER_DELAY_MS = 2_147_483_647;
// Pause after a background renewal fails for a reason other than the session ending.
export const TRANSIENT_RETRY_DELAY_MS = 60_000;

// Renews the token shortly before it expires so the user never meets a 401. The expiry comes
// from the JWT itself; nothing about token lifetimes is hard-coded. Every renewal goes through
// the same single-flight `TokenRefreshService.renew()` the 401 flow uses.
@Injectable({ providedIn: 'root' })
export class TokenRenewalSchedulerService implements OnDestroy {
  private readonly store = inject(Store);
  private readonly refresh = inject(TokenRefreshService);
  private readonly ngZone = inject(NgZone);
  private readonly token = this.store.selectSignal(authFeature.selectToken);
  private timer: ReturnType<typeof setTimeout> | null = null;
  private subscription: Subscription | null = null;

  start(): void {
    if (this.subscription) {
      return;
    }

    // Login, renewal, adoption from another tab and hydration all change the token; logout and
    // session end clear it, which cancels the timer.
    this.subscription = this.store
      .select(authFeature.selectToken)
      .pipe(distinctUntilChanged())
      .subscribe(() => this.arm());
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.subscription?.unsubscribe();
    this.subscription = null;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  // Timers are unreliable in background tabs and after the machine sleeps, so a tab that comes
  // back re-checks the expiry itself.
  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.evaluate();
    }
  };

  private arm(): void {
    this.clearTimer();
    const expiry = readJwtExpiry(this.token());

    if (expiry === null) {
      return;
    }

    this.schedule(Math.max(0, expiry - RENEW_BEFORE_EXPIRY_MS - Date.now()));
  }

  private schedule(delayMs: number): void {
    this.clearTimer();
    this.ngZone.runOutsideAngular(() => {
      this.timer = setTimeout(
        () => {
          this.timer = null;
          this.ngZone.run(() => this.evaluate());
        },
        Math.min(delayMs, MAX_TIMER_DELAY_MS),
      );
    });
  }

  // Decides from the current clock: renew when inside the window, otherwise wait for what is
  // left (the timer may have been clamped, or the tab may be coming back from the background).
  private evaluate(): void {
    const expiry = readJwtExpiry(this.token());

    if (expiry === null) {
      return;
    }

    const remaining = expiry - RENEW_BEFORE_EXPIRY_MS - Date.now();

    if (remaining > 0) {
      this.schedule(remaining);
      return;
    }

    // A token that looks due right after a renewal waits out the minimum interval.
    const wait = this.refresh.msUntilRenewalAllowed();

    if (wait > 0) {
      this.schedule(wait);
      return;
    }

    this.clearTimer();
    this.renewInBackground();
  }

  private renewInBackground(): void {
    this.refresh.renew(null, { silent: true }).subscribe((outcome) => {
      // A renewal changes the token, which re-arms the timer; a session end clears it. Only a
      // transient failure needs its own retry, with a pause so it cannot spin on the old expiry.
      if (outcome.status === 'transient' && this.token()) {
        this.schedule(TRANSIENT_RETRY_DELAY_MS);
      }
    });
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
